import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import './App.css';

// Customize the public Liberty style before tiles render, retaining attribution.
function campusStyle(previousStyle, style) {
  const withNumberFallback = (value) => {
    if (!Array.isArray(value)) return value;
    if (value[0] === 'get' && value[1] === 'rank') return ['coalesce', value, 0];
    if (value[0] === 'get' && value[1] === 'ref_length') return ['coalesce', value, 99];
    return value.map(withNumberFallback);
  };
  style.light = { anchor: 'viewport', color: '#fff8eb', intensity: 0.25 };
  style.layers.forEach((layer) => {
    if (layer.id === 'building-3d') {
      layer.paint = {
        ...layer.paint,
        'fill-extrusion-color': '#ece8dc',
        'fill-extrusion-opacity': 0.85,
        'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
        'fill-extrusion-height': ['coalesce', ['get', 'render_height'], 6],
      };
    }
    if (layer.id === 'park') layer.paint['fill-color'] = '#c4e5b2';
    if (layer.id === 'poi_transit') layer.layout.visibility = 'none';
    if (layer.id.startsWith('poi_r')) {
      layer.layout['icon-image'] = ['coalesce', ['image', layer.layout['icon-image']], ['image', 'circle_11']];
      layer.layout['text-font'] = ['Noto Sans Regular'];
    }
    if (layer.filter) layer.filter = withNumberFallback(layer.filter);
  });
  return style;
}

function Map() {
  const navigate = useNavigate();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [status, setStatus] = useState('loading');
  const [is3D, setIs3D] = useState(true);

  useEffect(() => {
    if (!mapContainerRef.current) {
      return undefined;
    }

    if (process.env.NODE_ENV === 'test') {
      return undefined;
    }

    let map;
    try {
      map = new maplibregl.Map({
        container: mapContainerRef.current,
        style: null,
        // Original tilt and bearing, framed around the Quad like the reference image.
        center: [-88.2253, 40.1076],
        zoom: 16.2,
        pitch: 55,
        bearing: 17.6,
        maxPitch: 70,
        maxBounds: [[-88.245, 40.093], [-88.213, 40.120]],
        attributionControl: { compact: true },
      });
    } catch (error) {
      setStatus('unsupported');
      return undefined;
    }
    mapRef.current = map;
    let mapLoaded = false;
    const loadingTimeout = window.setTimeout(() => setStatus('slow'), 20000);
    map.on('error', () => {
      // A single missing tile must not replace an otherwise usable map.
      if (!mapLoaded) setStatus('slow');
    });
    map.on('load', () => {
      mapLoaded = true;
      window.clearTimeout(loadingTimeout);
      setStatus('ready');
    });
    map.on('pitchend', () => setIs3D(map.getPitch() > 10));
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'bottom-right');
    map.setStyle('https://tiles.openfreemap.org/styles/liberty', { transformStyle: campusStyle });

    const audioLinks = [
      'https://p.scdn.co/mp3-preview/205de8d72d229b1a6d45da2f8b2b6f1933a1c943',
      'https://p.scdn.co/mp3-preview/c6fda69b34f56c5ac15921a8655a14e8c1405156',
      'https://p.scdn.co/mp3-preview/ccf922d4899ea93ec97445c85698e40581933608',
      'https://p.scdn.co/mp3-preview/0203d9e70bfeeb5a30cc96939f2ca92b140f1613',
      'https://p.scdn.co/mp3-preview/1ade60a13d1d2662d1d8ac8f85ca59c5d344df38',
      'https://p.scdn.co/mp3-preview/1ccf134b1783422db53b0e795b519296aa97c530',
      'https://p.scdn.co/mp3-preview/73fad68b4108659817910dbf3a08ea7c4a3b9086',
      'https://p.scdn.co/mp3-preview/5c5c328aa39d0a292ec2f55bc79832db3635292a',
      'https://p.scdn.co/mp3-preview/502f46d686f682920c1ecffd98fc7e7ae5a26ae0',
      'https://p.scdn.co/mp3-preview/1dac0398947b848882920a44ca00525428037dec',
      'https://p.scdn.co/mp3-preview/28b9037e5e58ac6612a46978437c510abc1ac5c6',
      'https://p.scdn.co/mp3-preview/05a30cdbf0d1193ac0a4ddd441258dbcee15ad7a',
      'https://p.scdn.co/mp3-preview/d06fc4106394f7c3bbd65070566d9dde31e472a2',
      'https://p.scdn.co/mp3-preview/4afff817e322eb0a89613ce488357f80a6f7c3f0',
      'https://p.scdn.co/mp3-preview/3477472e0fdd6f15e195526cd0a720989641af39',
      'https://p.scdn.co/mp3-preview/0d657b7a2ddad259ac6915cd653c296e05d96bc3',
      'https://p.scdn.co/mp3-preview/041ed6d7f8884403e76944ce963d1fb68e1b5338',
      'https://p.scdn.co/mp3-preview/1ff91fa90d03cf3e3e0252f9fada1cafbff678ff',
      'https://p.scdn.co/mp3-preview/49514c7846d6d391017bba23118a805c46104d4f',
      'https://p.scdn.co/mp3-preview/ccf922d4899ea93ec97445c85698e40581933608',
      'https://p.scdn.co/mp3-preview/42167ea77fc3d3066db07afa32007c9a41cdae9f',
      'https://p.scdn.co/mp3-preview/30806b0c2953942e5b8a7b86a32c4b8ba86e5337',
      'https://p.scdn.co/mp3-preview/611240bbd6c847ddf4637f9b1d65083307d93fdc',
      'https://p.scdn.co/mp3-preview/22b7a18d840604c24e60181f8759c2cea8848af3',
      'https://p.scdn.co/mp3-preview/112f1cab01dce4634d553f61c7930c02597f8a85',
      'https://p.scdn.co/mp3-preview/b252205927ac0caada94b2495d91931dd08a54f5',
      'https://p.scdn.co/mp3-preview/599489fb5052afa1d87e92784747c6ba96a380c4',
      'https://p.scdn.co/mp3-preview/32e27275ec894f06ee27b5f8cba45f94b82ba295',
      'https://p.scdn.co/mp3-preview/00ed7ccb5b0643f748dfb26bce67f9d5eba7945c',
      'https://p.scdn.co/mp3-preview/9bb2cc604c57151ff8e3db3f70597ada2bfcf6ec',
      'https://p.scdn.co/mp3-preview/0531c9dc003e0a8539c5ac00ec4fbd1969a3a2b9',
      'https://p.scdn.co/mp3-preview/aa2869bf2c34b7134b443c91531ee0d73f79a6aa',
      'https://p.scdn.co/mp3-preview/73b5ca267f20a5baa8bc5e543d0f62fcae4b17b4',
      'https://p.scdn.co/mp3-preview/c2fb2333c41f21ab2f1556cecfa73f6632eeed17',
      'https://p.scdn.co/mp3-preview/ff4e8d0ac5f27452568818961ca8ee5a95301c3c',
    ];

    let currentAudio = null;

    const stopAudio = () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        currentAudio = null;
      }
    };

    const playRandomAudio = () => {
      stopAudio();
      const randomAudioLink = audioLinks[Math.floor(Math.random() * audioLinks.length)];
      currentAudio = new Audio(randomAudioLink);
      currentAudio.play().catch(() => undefined);
    };

    const markers = [
      {
        coordinates: [-88.22722188356069, 40.109733625358196],
        title: 'Courtyard Cafe',
        info: 'Open to the public during Illini Union hours. Located in the Courtyard Cafe.',
        address: '1401 W Green St, Urbana, IL 61801',
        notes:
          'No, people will not be bothered. In fact, <a href="https://www.reddit.com/r/UIUC/comments/11000op/playing_piano_in_the_union/" target="_blank">they will appreciate your music.</a>',
      },
      {
        coordinates: [-88.22726823903758, 40.109307698523004],
        title: 'Basement of the Union',
        info: 'Open to the public during Illini Union hours. Located in the lower level of the Union.',
        address: '1401 W Green St, Urbana, IL 61801',
        notes:
          'No, people will not be bothered. In fact, <a href="https://www.reddit.com/r/UIUC/comments/11000op/playing_piano_in_the_union/" target="_blank">they will appreciate your music.</a>',
      },
      {
        coordinates: [-88.22728869785318, 40.10901155741101],
        title: 'South Lounge Union',
        info: 'Open to the public during Illini Union hours. Located in the South Lounge of the Union.',
        address: '1401 W Green St, Urbana, IL 61801',
        notes:
          'No, people will not be bothered. In fact, <a href="https://www.reddit.com/r/UIUC/comments/11000op/playing_piano_in_the_union/" target="_blank">they will appreciate your music.</a>',
      },
      {
        coordinates: [-88.22187657447314, 40.10978006861594],
        title: 'ISR',
        info: 'Located in the basement. Two pianos are available. 24/7',
        address: '1010 W Illinois St, Urbana, IL 61801',
        notes: 'Go to the front desk to ask for the key.',
      },
      {
        coordinates: [-88.22044258272953, 40.10004449667344],
        title: 'PAR',
        info: 'Two pianos in the lobby next to Saunders and Carr Hall. 24/7',
        address: '1001 W Pennsylvania Ave, Urbana, IL 61801',
        notes: 'Open and waiting for you. No key needed.',
      },
      {
        coordinates: [-88.2209848713531, 40.0989512974528],
        title: 'FAR',
        info: 'Located in the lounge area. 24/7',
        address: '1005 W College Ct, Urbana, IL 61801',
        notes: 'Open and waiting for you. No key needed.',
      },
      {
        coordinates: [-88.2203632959191, 40.10417198718885],
        title: 'LAR-Allen Hall',
        info: 'Many pianos available in the basement of Allen. LAR has a piano as well.',
        address: '1005 W Gregory Dr, Urbana, IL 61801',
        notes: 'Just walk around and you will find them.',
      },
      {
        coordinates: [-88.22186724345654, 40.110973619868645],
        title: 'Daniels Hall',
        info: 'Located in the lobby.',
        address: '1010 W Green St, Urbana, IL 61801',
        notes: '10 AM to 10 PM',
      },
      {
        coordinates: [-88.21984642280839, 40.11088009302455],
        title: 'Hendrick House',
        info: 'Located in the music practice room.',
        address: '904 W Green St, Urbana, IL 61801',
        notes: '🎶',
      },
      {
        coordinates: [-88.23235783815069, 40.10707440486369],
        title: 'Sherman Hall',
        info: 'Located in the lobby.',
        address: '909 S 5th St, Champaign, IL 61820',
        notes: '🎶',
      },
      {
        coordinates: [-88.22328008005938, 40.10627892981786],
        title: 'Music Building',
        info: 'Many pianos available in the basement and second floor practice rooms.',
        address: '1114 W Nevada St, Urbana, IL 61801',
        notes:
          '<a href="https://apps.atlas.illinois.edu/Music_Scheduler/Calendars/Calendars.aspx" target="_blank">Check calendars</a> to see when rooms are open. Befriend a music student and they can book rooms for you.',
      },
      {
        coordinates: [-88.22603986876098, 40.10573343216398],
        title: 'Smith Memorial Hall',
        info: 'Two pianos in the main auditorium. Lots pianos in the third floor and basement practice rooms.',
        address: '805 S Mathews Ave, Urbana, IL 61801',
        notes: 'Walk around and you will find many pianos all over this building.',
      },
      {
        coordinates: [-88.22987628364767, 40.106021804903015],
        title: 'Newman Hall',
        info: 'A few pianos available in the basement.',
        address: '604 E Armory Ave, Champaign, IL 61820',
        notes: '🎶',
      },
      {
        coordinates: [-88.23230035260188, 40.106421984951076],
        title: 'Illini Tower',
        info: 'Located in the basement. 24/7',
        address: '409 E Chalmers St, Champaign, IL 61820',
        notes: 'Scan access required.',
      },
      {
        coordinates: [-88.22529271694673, 40.10678044284171],
        title: 'Channing Murray Foundation',
        info: 'First floor. Whenever the building is open.',
        address: '1209 W Oregon St, Urbana, IL 61801',
        notes: '🎶',
      },
      {
        coordinates: [-88.22629939666987, 40.11445955881165],
        title: 'Civil Engineering Bridge',
        info: 'Located in the third floor bridge. 24/7',
        address: '301 N Mathews Ave, Urbana, IL 61801',
        notes: '🎶',
      },
      {
        coordinates: [-88.22440023889716, 40.10576117398954],
        title: 'La Casa',
        info: 'You are welcome to play during open hours.',
        address: '1203 W Nevada St, Urbana, IL 61801',
        notes: '🎶',
      },
      {
        coordinates: [-88.23550208386449, 40.10365381306705],
        title: 'SDRP',
        info: 'Go to the front desk to ask for the key.',
        address: 'Student Dining and Residential Programs Building, 301 E Gregory Dr 1st Floor, Champaign, IL 61820',
        notes: 'The IKE, in general, has many pianos. Explore!',
      },
      {
        coordinates: [-88.23787957141482, 40.103491219012795],
        title: 'Wasaja Hall',
        info: 'Located in the music room.',
        address: '1202 S 1st St, Champaign, IL 61820',
        notes: '🎶',
      },
      {
        coordinates: [-88.23684532310936, 40.101924268994516],
        title: 'Scott Hall',
        info: 'Located in the music room.',
        address: '202 E Peabody Dr, Champaign, IL 61820',
        notes: '🎶',
      },
      {
        coordinates: [-88.2245866535941, 40.11012429837601],
        title: 'Wesley Church',
        info: 'Available for anyone to play during open hours.',
        address: '1203 W Green St, Urbana, IL 61801',
        notes: 'One in the lounge area next to Etc. Coffeehouse, and another on the second floor',
      },
      {
        coordinates: [-88.22911136385936, 40.103263465750786],
        title: 'Architecture Building',
        info: 'Located in Temple Buell Architecture Gallery.',
        address: '608 Lorado Taft Dr, Champaign, IL 61820',
        notes: '🎶',
      },
      {
        coordinates: [-88.23425098523862, 40.10362453307273],
        title: 'Barton/Lundgren Hall',
        info: 'Located in the music room. Basement',
        address: '1205 S 4th St, Champaign, IL 61820',
        notes: '🎶',
      },
      {
        coordinates: [-88.22293329399727, 40.10803648801341],
        title: 'Krannert Center',
        info: 'Many pianos. Go downstairs and explore!',
        address: '500 S Goodwin Ave, Urbana, IL 61801',
        notes: 'Walk around and you will find many pianos all over this building.',
      },
      {
        coordinates: [-88.22922567268735, 40.10663907316668],
        title: 'University YMCA',
        info: 'Available for anyone to play during open hours.',
        address: '1001 S Wright St, Champaign, IL 61820',
        notes: '🎶',
      },
      {
        coordinates: [-88.22300925371526, 40.10567783743967],
        title: 'Busey/Evans Hall',
        info: 'Located in the music rooms.',
        address: '1115 W Nevada St, Urbana, IL 61801',
        notes: '🎶',
      },
      {
        coordinates: [-88.23492261995621, 40.10706448345761],
        title: 'Bromley Hall',
        info: 'Beautiful piano.',
        address: '910 S 3rd St, Champaign, IL 61820',
        notes: '🎶',
      },
      {
        coordinates: [-88.23269462644456, 40.1082919248735],
        title: 'Presby Hall',
        info: 'Located in the music room.',
        address: '805 S 5th St, Champaign, IL 61820',
        notes: '🎶',
      },
      {
        coordinates: [-88.21995964607402, 40.10802364981988],
        title: 'Alumni Center',
        info: 'Located in the lobby or in the ballroom.',
        address: 'Alice Campbell Alumni Center, 601 S Lincoln Ave, Urbana, IL 61801',
        notes: 'The Provost once thanked me for playing the piano here.',
      },
      {
        coordinates: [-88.22033078602954, 40.10621315041691],
        title: 'Chez Veterans Center',
        info: 'Located in the first floor.',
        address: '908 W Nevada St, Urbana, IL 61801',
        notes: 'Available during open hours.',
      },
      {
        coordinates: [-88.22913937785624, 40.110189510509414],
        title: 'Mia Zas',
        info: 'Located on the second floor.',
        address: '629 E Green St #5701, Champaign, IL 618201',
        notes: 'Available during open hours.',
      },
      {
        coordinates: [-88.23070148289597, 40.104724555292435],
        title: 'Harding Band Building',
        info: 'Located on the first floor, room 141',
        address: '1103 S 6th St, Champaign, IL 61820',
        notes: 'There is a xylophone on the second floor inside the museum.',
      },
    ];

    let activePopup = null;

    markers.forEach((marker) => {
      const encodedAddress = encodeURIComponent(marker.address);

      const popupContent = `
        <div class="popup-content">
          <div class="popup-title">${marker.title}</div>
          <div class="popup-description-label">Description</div>
          <div class="popup-info">${marker.info}</div>
          <div class="popup-address-label">Address</div>
          <div class="popup-address"><a href="https://www.google.com/maps/search/?api=1&query=${encodedAddress}" target="_blank" rel="noreferrer">${marker.address}</a></div>
          <div class="popup-description-label">Notes</div>
          <div class="popup-info">${marker.notes}</div>
        </div>
      `;

      const element = document.createElement('button');
      element.className = 'piano-marker';
      element.type = 'button';
      element.setAttribute('aria-label', marker.title);
      element.title = marker.title;
      // MapLibre handles keyboard activation; suppress the button's second click.
      element.addEventListener('keypress', (event) => {
        if (event.code === 'Enter' || event.code === 'Space') event.preventDefault();
      });
      const icon = document.createElement('img');
      icon.src = `${process.env.PUBLIC_URL}/piano2.png`;
      icon.alt = '';
      icon.draggable = false;
      element.appendChild(icon);

      const popup = new maplibregl.Popup({ offset: 28, maxWidth: '320px' })
        .setHTML(popupContent);
      popup.on('open', () => {
        if (activePopup && activePopup !== popup) activePopup.remove();
        activePopup = popup;
        playRandomAudio();
      });
      popup.on('close', () => {
        if (activePopup === popup) {
          stopAudio();
          activePopup = null;
        }
      });

      new maplibregl.Marker({ element, anchor: 'bottom' })
        .setLngLat(marker.coordinates)
        .setPopup(popup)
        .addTo(map);
    });

    return () => {
      window.clearTimeout(loadingTimeout);
      stopAudio();
      map.remove();
      mapRef.current = null;
    };
  }, [navigate]);

  const resetView = () => mapRef.current?.easeTo({
    center: [-88.2253, 40.1076], zoom: 16.2, pitch: 55, bearing: 17.6,
  });

  return (
    <main className="map-page">
      <div id="map" ref={mapContainerRef} aria-label="Map of pianos around the UIUC campus" />
      <nav className="map-actions" aria-label="Map controls">
        <button type="button" onClick={resetView}>Campus</button>
        <button type="button" aria-label="3D view" aria-pressed={is3D}
          onClick={() => mapRef.current?.easeTo({ pitch: is3D ? 0 : 55 })}>3D</button>
        <button type="button" className="about-button" onClick={() => navigate('/about')}>About</button>
      </nav>
      {status !== 'ready' && (
        <div className="map-status" role="status">
          {status === 'loading' ? 'Finding the pianos…' : (
            <>
              <p>{status === 'unsupported'
                ? 'This browser could not open the 3D map. Try another browser or enable graphics acceleration.'
                : 'The map is taking longer to load. Check your connection and try again.'}</p>
              <button type="button" onClick={() => window.location.reload()}>Try again</button>
              <a href="https://www.openstreetmap.org/#map=16/40.1058/-88.2278" target="_blank" rel="noreferrer">Open campus map</a>
            </>
          )}
        </div>
      )}
    </main>
  );
}

export default Map;
